import { GitPullRequestIcon } from "lucide-react";
import type { CmmdForgeAutomationRun } from "@cmmd-forge/contracts";

import { formatRelativeTimeLabel } from "~/timestampFormat";
import {
  ProjectAutomationStatusDot,
  type ProjectAutomationStatusTone,
} from "./ProjectAutomationStatusDot";

export interface ProjectAutomationRunsSectionProps {
  readonly runs: readonly CmmdForgeAutomationRun[];
  readonly onSelectRun: ((run: CmmdForgeAutomationRun) => void) | undefined;
}

function statusTone(status: string): ProjectAutomationStatusTone {
  if (status === "failed") return "failed";
  if (status === "running" || status === "queued") return "running";
  if (status === "done" || status === "needs_review") return "ready";
  return "neutral";
}

export function ProjectAutomationRunsSection({
  runs,
  onSelectRun,
}: ProjectAutomationRunsSectionProps) {
  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-sm font-semibold text-foreground">Runs</h2>
      </div>
      {runs.length === 0 ? (
        <div className="border-y border-border/60 py-5 text-sm text-muted-foreground">
          Run history will appear here after a trigger or schedule fires.
        </div>
      ) : (
        <div className="divide-y divide-border/55 border-y border-border/60">
          {runs.map((entry) => {
            const sourceTitle =
              typeof entry.run.sourceRef["title"] === "string"
                ? entry.run.sourceRef["title"]
                : null;
            const prUrl =
              typeof entry.run.targetRef["prUrl"] === "string"
                ? entry.run.targetRef["prUrl"]
                : null;
            return (
              <div
                key={entry.run.id}
                className="grid min-w-0 gap-2 py-4 md:grid-cols-[minmax(0,1fr)_auto]"
              >
                <button
                  type="button"
                  aria-label={`Open run ${entry.run.id}`}
                  disabled={!onSelectRun}
                  onClick={() => onSelectRun?.(entry)}
                  className="min-w-0 rounded-md text-left outline-none hover:bg-muted/25 focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:hover:bg-transparent"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <ProjectAutomationStatusDot tone={statusTone(entry.run.status)} />
                    <div className="min-w-0 truncate text-sm font-medium text-foreground">
                      {entry.rule.name}
                    </div>
                  </div>
                  <div className="mt-1 ml-5 truncate text-xs text-muted-foreground">
                    {sourceTitle ?? entry.run.sourceType}
                  </div>
                  {entry.run.failureMessage ? (
                    <div className="mt-2 ml-5 text-xs text-destructive-foreground">
                      {entry.run.failureMessage}
                    </div>
                  ) : null}
                </button>
                <div className="ml-5 flex min-w-0 items-center gap-3 text-xs text-muted-foreground md:ml-0">
                  <span className="capitalize">{entry.run.status.replaceAll("_", " ")}</span>
                  <span>{formatRelativeTimeLabel(entry.run.createdAt)}</span>
                  {prUrl ? (
                    <a
                      href={prUrl}
                      className="inline-flex items-center gap-1 text-foreground underline-offset-4 hover:underline"
                    >
                      <GitPullRequestIcon className="size-3.5" />
                      PR
                    </a>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
